import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex } from "@noble/hashes/utils.js";

import type { ExactAssetRef, ExactRuntimeAssetsManifest } from "./exact-runtime-loader";
import { exactPhysicalRunId } from "./physical-run-id";

const RELEASE_ROOT = "prime-mach1-physical-releases";
const SHA256 = /^[0-9a-f]{64}$/u;
const RELEASE_PATHS = [
  "acceptance/full-pwa-acceptance.json",
  "acceptance/runtime-receipt.json",
  "acceptance/runtime-validator-result.json",
  "acceptance/prime-template-golden.json",
  "acceptance/webgpu-standalone.json",
  "acceptance/webgpu-integration.json",
  "acceptance/prime-event-trace.json",
  "acceptance/decoder-token-trace.json",
  "acceptance/decoder-transaction-trace.json",
  "acceptance/kernel-transcript.json",
  "acceptance/child-message-bundle.json",
  "acceptance/offline-network-log.json",
  "acceptance/app-audit-log.json",
  "acceptance/runtime-snapshot.json",
  "acceptance/capability-import-audit.json",
] as const;

interface ReleaseReference {
  path: string;
  byteCount: number;
  sha256: string;
}

interface PhysicalReleaseJson {
  kind: "prime-mach1-physical-pwa-release";
  schemaVersion: 1;
  qualified: true;
  runId: string;
  artifacts: ReleaseReference[];
  verification: {
    accepted: true;
    artifactSetSha256: string;
    verifierBuildSha256: string;
  };
}

interface PhysicalArtifact extends ReleaseReference {
  bytes: Uint8Array;
}

interface PhysicalReleaseBundle {
  runId: string;
  release: PhysicalReleaseJson;
  releaseBytes: Uint8Array;
  releaseSha256: string;
  artifacts: PhysicalArtifact[];
}

export interface PhysicalReleaseDownload {
  kind: "prime-mach1-user-authorized-release-export";
  schemaVersion: 1;
  synthetic: false;
  exportedAt: string;
  runId: string;
  verifierBuildSha256: string;
  releaseSha256: string;
  entries: Array<ReleaseReference & {
    commitOrder: number;
    bytesBase64: string;
  }>;
}

function canonicalJson(value: unknown): string {
  const seen = new Set<object>();
  const normalize = (entry: unknown): unknown => {
    if (entry === null || typeof entry === "string" || typeof entry === "boolean") return entry;
    if (typeof entry === "number") {
      if (!Number.isFinite(entry)) throw new Error("canonical JSON rejects non-finite numbers");
      return entry;
    }
    if (typeof entry === "undefined") return undefined;
    if (Array.isArray(entry)) return entry.map((item) => normalize(item) ?? null);
    if (typeof entry !== "object") throw new Error(`canonical JSON rejects ${typeof entry}`);
    if (seen.has(entry)) throw new Error("canonical JSON rejects cycles");
    seen.add(entry);
    const output: Record<string, unknown> = {};
    for (const key of Object.keys(entry).sort()) {
      const normalized = normalize((entry as Record<string, unknown>)[key]);
      if (normalized !== undefined) output[key] = normalized;
    }
    seen.delete(entry);
    return output;
  };
  return `${JSON.stringify(normalize(value))}\n`;
}

async function getDirectory(
  root: FileSystemDirectoryHandle,
  segments: readonly string[],
): Promise<FileSystemDirectoryHandle> {
  let current = root;
  for (const segment of segments) current = await current.getDirectoryHandle(segment);
  return current;
}

async function readPath(root: FileSystemDirectoryHandle, relative: string): Promise<Uint8Array> {
  const segments = relative.split("/");
  const fileName = segments.pop();
  if (!fileName || segments.some((segment) => !segment || segment === "." || segment === "..")) {
    throw new Error(`Unsafe physical release path ${relative}`);
  }
  const directory = await getDirectory(root, segments);
  const file = await directory.getFileHandle(fileName).then((handle) => handle.getFile());
  return new Uint8Array(await file.arrayBuffer());
}

async function latestRunDirectory(): Promise<{ runId: string; directory: FileSystemDirectoryHandle }> {
  if (!navigator.storage?.getDirectory) throw new Error("OPFS is unavailable");
  const root = await navigator.storage.getDirectory();
  const releases = await root.getDirectoryHandle(RELEASE_ROOT);
  const iterable = releases as unknown as {
    entries(): AsyncIterableIterator<[string, FileSystemHandle]>;
  };
  const candidates: Array<{ runId: string; directory: FileSystemDirectoryHandle; capturedAt: string }> = [];
  for await (const [runId, handle] of iterable.entries()) {
    if (handle.kind !== "directory") continue;
    try {
      exactPhysicalRunId(runId);
      const directory = handle as FileSystemDirectoryHandle;
      const bytes = await readPath(directory, "release.json");
      const release = JSON.parse(new TextDecoder().decode(bytes)) as { capturedAt?: string };
      if (typeof release.capturedAt === "string") candidates.push({ runId, directory, capturedAt: release.capturedAt });
    } catch {
      // Interrupted directories have no release marker and are never exports.
    }
  }
  candidates.sort((left, right) => right.capturedAt.localeCompare(left.capturedAt));
  const latest = candidates[0];
  if (!latest) throw new Error("No independently verified OPFS physical release is available to export");
  return latest;
}

async function requestedRunDirectory(runId?: string): Promise<{
  runId: string;
  directory: FileSystemDirectoryHandle;
}> {
  if (!runId) return latestRunDirectory();
  const selectedRunId = exactPhysicalRunId(runId);
  if (!navigator.storage?.getDirectory) throw new Error("OPFS is unavailable");
  const root = await navigator.storage.getDirectory();
  const releases = await root.getDirectoryHandle(RELEASE_ROOT);
  return { runId: selectedRunId, directory: await releases.getDirectoryHandle(selectedRunId) };
}

async function loadBundle(runId?: string): Promise<PhysicalReleaseBundle> {
  const { runId: selectedRunId, directory } = await requestedRunDirectory(runId);
  const releaseBytes = await readPath(directory, "release.json");
  const release = JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(releaseBytes)) as PhysicalReleaseJson;
  if (
    release.kind !== "prime-mach1-physical-pwa-release" ||
    release.schemaVersion !== 1 ||
    release.qualified !== true ||
    release.runId !== selectedRunId ||
    release.verification?.accepted !== true ||
    !SHA256.test(release.verification.artifactSetSha256) ||
    !SHA256.test(release.verification.verifierBuildSha256) ||
    release.artifacts?.length !== RELEASE_PATHS.length ||
    canonicalJson(release) !== new TextDecoder().decode(releaseBytes)
  ) throw new Error("OPFS release marker is not a canonical exact physical release");
  const paths = release.artifacts.map((artifact) => artifact.path);
  if (new Set(paths).size !== RELEASE_PATHS.length || !RELEASE_PATHS.every((path) => paths.includes(path))) {
    throw new Error("OPFS release does not contain the exact 15 evidence artifacts");
  }
  const artifacts: PhysicalArtifact[] = [];
  for (const reference of release.artifacts) {
    if (!Number.isSafeInteger(reference.byteCount) || reference.byteCount < 0 || !SHA256.test(reference.sha256)) {
      throw new Error(`Invalid release reference ${reference.path}`);
    }
    const bytes = await readPath(directory, reference.path);
    if (bytes.byteLength !== reference.byteCount || bytesToHex(sha256(bytes)) !== reference.sha256) {
      throw new Error(`OPFS release artifact identity failed: ${reference.path}`);
    }
    artifacts.push({ ...reference, bytes });
  }
  return {
    runId: selectedRunId,
    release,
    releaseBytes,
    releaseSha256: bytesToHex(sha256(releaseBytes)),
    artifacts,
  };
}

async function fetchExact(ref: ExactAssetRef): Promise<Uint8Array> {
  const response = await fetch(ref.path, { cache: "no-store", credentials: "same-origin" });
  if (!response.ok) throw new Error(`Verifier asset failed (${response.status})`);
  const bytes = new Uint8Array(await response.arrayBuffer());
  if (bytes.byteLength !== ref.byteCount || bytesToHex(sha256(bytes)) !== ref.sha256) {
    throw new Error(`Verifier asset identity failed: ${ref.path}`);
  }
  return bytes;
}

async function independentlyVerify(bundle: PhysicalReleaseBundle): Promise<void> {
  const response = await fetch("/runtime/runtime-assets.json", { cache: "no-store" });
  if (!response.ok) throw new Error(`Runtime verifier binding failed (${response.status})`);
  const runtime = await response.json() as ExactRuntimeAssetsManifest;
  const manifestRef = runtime.physicalVerifier?.manifest;
  const entryRef = runtime.physicalVerifier?.entry;
  if (!manifestRef || !entryRef || bundle.release.verification.verifierBuildSha256 !== entryRef.sha256) {
    throw new Error("OPFS release verifier build is not the staged hash-pinned verifier");
  }
  const [manifestBytes, entryBytes] = await Promise.all([fetchExact(manifestRef), fetchExact(entryRef)]);
  const manifest = JSON.parse(new TextDecoder().decode(manifestBytes)) as {
    entry?: string;
    assets?: Array<{ path?: string; byteCount?: number; sha256?: string }>;
  };
  if (
    manifest.assets?.length !== 1 ||
    manifest.entry !== manifest.assets[0]?.path ||
    entryRef.path.split("/").at(-1) !== manifest.entry ||
    manifest.assets[0]?.byteCount !== entryRef.byteCount ||
    manifest.assets[0]?.sha256 !== entryRef.sha256
  ) throw new Error("Physical verifier nested manifest binding failed");
  const url = URL.createObjectURL(new Blob([Uint8Array.from(entryBytes)], { type: "text/javascript" }));
  const worker = new Worker(url, { type: "module", name: "prime-mach1-export-reverify" });
  const id = crypto.randomUUID();
  try {
    const result = await new Promise<Record<string, unknown>>((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error("Export verifier timed out")), 120_000);
      worker.onerror = (event) => {
        clearTimeout(timeout);
        reject(new Error(event.message || "Export verifier crashed"));
      };
      worker.onmessage = (event: MessageEvent<Record<string, unknown>>) => {
        if (event.data?.id !== id) return;
        clearTimeout(timeout);
        if (event.data.type === "verification-result") resolve(event.data.result as Record<string, unknown>);
        else reject(new Error(String(event.data.error ?? "Export verifier failed")));
      };
      worker.postMessage({
        id,
        type: "verify-release",
        verifierBuildSha256: entryRef.sha256,
        bundle,
      });
    });
    if (
      result.accepted !== true ||
      result.verifierBuildSha256 !== entryRef.sha256 ||
      result.artifactSetSha256 !== bundle.release.verification.artifactSetSha256
    ) throw new Error(`Independent export reverification rejected: ${String(result.failures ?? "unknown failure")}`);
  } finally {
    worker.terminate();
    URL.revokeObjectURL(url);
  }
}

function base64(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunk) {
    binary += String.fromCharCode(...bytes.subarray(offset, Math.min(bytes.length, offset + chunk)));
  }
  return btoa(binary);
}

export async function buildVerifiedPhysicalReleaseExport(
  runId?: string,
): Promise<PhysicalReleaseDownload> {
  const bundle = await loadBundle(runId);
  await independentlyVerify(bundle);
  const entries = bundle.artifacts.map((artifact, index) => ({
    path: artifact.path,
    byteCount: artifact.byteCount,
    sha256: artifact.sha256,
    commitOrder: index + 1,
    bytesBase64: base64(artifact.bytes),
  }));
  entries.push({
    path: "release.json",
    byteCount: bundle.releaseBytes.byteLength,
    sha256: bundle.releaseSha256,
    commitOrder: entries.length + 1,
    bytesBase64: base64(bundle.releaseBytes),
  });
  const download: PhysicalReleaseDownload = {
    kind: "prime-mach1-user-authorized-release-export",
    schemaVersion: 1,
    synthetic: false,
    exportedAt: new Date().toISOString(),
    runId: bundle.runId,
    verifierBuildSha256: bundle.release.verification.verifierBuildSha256,
    releaseSha256: bundle.releaseSha256,
    entries,
  };
  return download;
}

export async function exportLatestVerifiedPhysicalRelease(): Promise<string> {
  const download = await buildVerifiedPhysicalReleaseExport();
  const blob = new Blob([JSON.stringify(download)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${download.runId}.prime-mach1-release.json`;
    anchor.rel = "noopener";
    anchor.click();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
  return download.runId;
}
